import { useState } from 'react';
import { Store, Globe } from 'lucide-react';
import { FormField, Input, Select } from '@/components/ui';
import type { WizardData } from '../types';
import { normalizeSubdomain, checkSubdomainAvailability } from '../utils';

interface StepShopProps {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
}

/**
 * Step 2: Shop Basics
 * Collects shop name, language, and subdomain
 */
export function StepShop({ data, onChange }: StepShopProps) {
  const [touched, setTouched] = useState({ shopName: false, subdomain: false });

  const normalizedSubdomain = normalizeSubdomain(data.subdomain);
  const isAvailable = checkSubdomainAvailability(normalizedSubdomain);

  const shopNameError =
    touched.shopName && data.shopName.trim().length === 0
      ? 'Shop name is required'
      : undefined;

  const subdomainError = data.subdomain
    ? isAvailable
      ? undefined
      : 'This subdomain is taken or too short'
    : touched.subdomain
      ? 'Shop URL is required'
      : undefined;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField label="Shop Name" required error={shopNameError}>
          <Input
            icon={Store}
            type="text"
            placeholder="e.g. MyShop"
            autoComplete="off"
            value={data.shopName}
            onChange={(e) => onChange({ shopName: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, shopName: true }))}
          />
        </FormField>

        <FormField label="Language" hint="Recommended">
          <Select
            value={data.language}
            onChange={(e) => onChange({ language: e.target.value })}
          >
            <option value="en">English</option>
            <option value="de">German</option>
          </Select>
        </FormField>
      </div>

      <FormField
        label="Shop URL (Subdomain)"
        required
        hint="You can add a custom domain later."
        error={subdomainError}
      >
        <div className="flex">
          <div className="min-w-0 flex-1">
            <Input
              icon={Globe}
              type="text"
              placeholder="myshop"
              autoComplete="off"
              value={data.subdomain}
              className="rounded-r-none"
              onChange={(e) =>
                onChange({
                  // Partial sanitization while typing (spaces → hyphens, remove invalid)
                  subdomain: e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, '-'),
                })
              }
              onBlur={() => {
                setTouched((t) => ({ ...t, subdomain: true }));
                // Full normalization on blur (removes double/leading/trailing hyphens)
                onChange({ subdomain: normalizedSubdomain });
              }}
            />
          </div>
          <span className="flex shrink-0 items-center rounded-r-control bg-paper-3 px-3.5 text-sm text-mute ring-1 ring-inset ring-line">
            .designdeck.shop
          </span>
        </div>
        {data.subdomain && (
          <p className="flex items-center gap-2 text-xs">
            <span
              className={
                isAvailable
                  ? 'font-medium text-success'
                  : 'font-medium text-danger'
              }
            >
              {isAvailable ? 'Available' : 'Taken'}
            </span>
            <span className="text-mute">
              {normalizedSubdomain}.designdeck.shop
            </span>
          </p>
        )}
      </FormField>
    </div>
  );
}
